/**
 * Placement d'un menu contextuel sous — ou au-dessus de — son bouton.
 *
 * Le menu était posé sous son bouton sans qu'on regarde s'il y tenait. Sur la
 * dernière trace d'une liste, ses dernières entrées sortaient de l'écran :
 * invisibles, et sans rien pour y accéder, `.menu` n'ayant ni hauteur maximale
 * ni débordement.
 *
 * Le calcul vit ici plutôt que dans le consommateur parce qu'il est
 * entièrement géométrique, donc éprouvable sans DOM — la mise en page, elle,
 * se mesure par une sonde (`scripts/track-menu.mjs`).
 *
 * Toutes les entrées sont des boîtes **réellement dessinées**, pas des
 * constantes du style : la hauteur d'un menu dépend du nombre d'entrées, de la
 * police et de la langue, et la déduire reviendrait à vérifier une copie de la
 * mise en page.
 */

/** Marge conservée entre le menu et le bord de son conteneur. */
export const MENU_MARGIN = 8

/** Écart entre le menu et le bouton qui l'ouvre. */
export const MENU_GAP = 4

export interface MenuPlacementInput {
  /** Boîte du bouton, en coordonnées du viewport. */
  readonly anchor: { readonly top: number; readonly bottom: number; readonly right: number }
  /** Boîte du menu telle qu'il vient d'être dessiné. */
  readonly menu: { readonly width: number; readonly height: number }
  /**
   * Boîte du conteneur qui borne le menu, en coordonnées du viewport. C'est
   * aussi le bloc auquel `top` et `left` se rapportent : les deux repères sont
   * lus ensemble pour que le calcul reste juste si ce conteneur cesse un jour
   * de couvrir l'écran entier.
   */
  readonly bounds: {
    readonly top: number
    readonly bottom: number
    readonly left: number
    readonly width: number
  }
}

export interface MenuPlacement {
  /** Décalages relatifs au conteneur. */
  readonly top: number
  readonly left: number
  /**
   * Hauteur maximale à imposer, avec défilement. `null` quand le menu tient
   * entier : ne rien poser vaut mieux qu'une contrainte inutile, qui ferait
   * apparaître une barre de défilement sur un menu qui n'en a pas besoin.
   */
  readonly maxHeight: number | null
  /** Vrai quand le menu a été retourné au-dessus du bouton. */
  readonly flipped: boolean
}

export function placeMenu(input: MenuPlacementInput): MenuPlacement {
  const { anchor, menu, bounds } = input

  const roomBelow = bounds.bottom - MENU_MARGIN - (anchor.bottom + MENU_GAP)
  const roomAbove = anchor.top - MENU_GAP - (bounds.top + MENU_MARGIN)

  // Au-dessus seulement si le menu n'a pas la place en dessous **et** qu'il y
  // gagne. Retourner dès que le dessous est juste ferait sauter le menu d'un
  // côté à l'autre pour un pixel, là où il tenait très bien.
  const flipped = menu.height > roomBelow && roomAbove > roomBelow

  const room = Math.max(0, flipped ? roomAbove : roomBelow)

  // Ni au-dessus ni en dessous : le menu défile plutôt que d'être rogné. Un
  // rognage ne se voit pas — c'est précisément ce qui rendait le défaut
  // silencieux.
  const maxHeight = menu.height > room ? room : null
  const height = Math.min(menu.height, room)

  const top = flipped
    ? anchor.top - MENU_GAP - height - bounds.top
    : anchor.bottom + MENU_GAP - bounds.top

  // Bord droit du menu aligné sur celui du bouton, puis ramené dans le
  // conteneur. La largeur vient de la mesure et non du `min-width` du style :
  // une copie de plus finirait par diverger.
  const left = Math.max(
    MENU_MARGIN,
    Math.min(
      anchor.right - menu.width - bounds.left,
      bounds.width - menu.width - MENU_MARGIN,
    ),
  )

  return { top, left, maxHeight, flipped }
}
